import { useMemo, useState } from "react";

import { useOptionalMessageContext } from "../../hooks/MessageContext";
import { MessageRole, MessageRow } from "../../api/message.type";
import { useSendNotificationPayload } from "../../hooks/notifications/useSendNotificationPayload";
import { useLoadContacts } from "../../hooks/useLoadContacts";
import { useLoadGroupChats } from "../../hooks/useLoadGroupChats";
import useUserDetails from "../../hooks/useUserDetails";
import { NOTIFICATION_EXAMPLES } from "../../medical/notification/notification.examples";
import { MedicalNotificationPayload } from "../../medical/notification/notification.types";
import { SubmitButton } from "../shared/buttons/submit-button";
import Card from "../shared/card";
import Typography from "../shared/typography";

const exampleLabels: Record<string, string> = {
  default: "Default test",
  assistantMessage: "Assistant message",
  dependentUpload: "Dependent upload",
  medicalRecordSummary: "Medical record AI summary",
  groupChatInvite: "Group chat invite",
  userRelationshipInvite: "Relationship invite",
  medicalTranscriptionShare: "Transcription share",
  ambientConversationShare: "Ambient conversation share",
};

export default function NotificationExamplesCard() {
  const { mutate, isPending } = useSendNotificationPayload();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const { groupChats } = useLoadGroupChats();
  const { contacts } = useLoadContacts();
  const { userDetails } = useUserDetails();
  const messageContext = useOptionalMessageContext();
  const messages = messageContext?.messages;

  const adminName = useMemo(() => {
    if (!userDetails) return undefined;
    const full = `${userDetails.first_name ?? ""} ${
      userDetails.last_name ?? ""
    }`.trim();
    return full || userDetails.email || undefined;
  }, [userDetails]);

  const serverMessages = useMemo(() => {
    if (!messages || !Array.isArray(messages)) return [];
    return messages.filter(Boolean) as MessageRow[];
  }, [messages]);

  const assistantMessage = useMemo(() => {
    return serverMessages.find(
      (message) => message.role === MessageRole.ASSISTANT
    );
  }, [serverMessages]);

  const latestUserMessage = useMemo(() => {
    for (let i = serverMessages.length - 1; i >= 0; i -= 1) {
      const message = serverMessages[i];
      if (message.role === MessageRole.USER) {
        return message;
      }
    }
    return undefined;
  }, [serverMessages]);

  const examples = useMemo(() => {
    const firstChat = groupChats?.[0];
    const firstContact = contacts?.[0];
    const overrides: Partial<Record<string, MedicalNotificationPayload>> = {};

    if (firstChat) {
      const groupChatLink = `${window.location.origin}/group-chat/${firstChat.id}`;
      overrides.default = {
        ...NOTIFICATION_EXAMPLES.default,
        entityId: firstChat.id,
      };
      overrides.assistantMessage = {
        ...NOTIFICATION_EXAMPLES.assistantMessage,
        entityId: firstChat.id,
        metadata: {
          ...NOTIFICATION_EXAMPLES.assistantMessage.metadata,
          messageContent: assistantMessage?.content || "Assistant response",
          assistantMessageId: assistantMessage?.id,
          userMessageId:
            assistantMessage?.reply_to_message_id || latestUserMessage?.id,
        },
      };
      overrides.groupChatInvite = {
        ...NOTIFICATION_EXAMPLES.groupChatInvite,
        entityId: firstChat.id,
        metadata: {
          ...NOTIFICATION_EXAMPLES.groupChatInvite.metadata,
          groupChatId: firstChat.id,
          groupChatName: firstChat.name ?? "Group chat",
          groupChatLink,
          inviterName: adminName ?? "Admin",
        },
      };
    }

    if (assistantMessage && !firstChat && assistantMessage.group_chat_id) {
      overrides.default = {
        ...NOTIFICATION_EXAMPLES.default,
        entityId: assistantMessage.group_chat_id,
      };
      overrides.assistantMessage = {
        ...NOTIFICATION_EXAMPLES.assistantMessage,
        entityId: assistantMessage.group_chat_id,
        metadata: {
          ...NOTIFICATION_EXAMPLES.assistantMessage.metadata,
          messageContent: assistantMessage.content,
          assistantMessageId: assistantMessage.id,
          userMessageId:
            assistantMessage.reply_to_message_id || latestUserMessage?.id,
        },
      };
    }

    if (firstContact) {
      overrides.userRelationshipInvite = {
        ...NOTIFICATION_EXAMPLES.userRelationshipInvite,
        entityId: firstContact.id,
        metadata: {
          ...NOTIFICATION_EXAMPLES.userRelationshipInvite.metadata,
          inviterName: adminName ?? "Admin",
          relationship:
            firstContact.relationship_type ||
            NOTIFICATION_EXAMPLES.userRelationshipInvite.metadata?.relationship,
        },
      };
    }

    return Object.entries(NOTIFICATION_EXAMPLES).map(
      ([key, payload]: [string, MedicalNotificationPayload]) => [
        key,
        (overrides[key] ?? payload) as MedicalNotificationPayload,
      ]
    ) as Array<[string, MedicalNotificationPayload]>;
  }, [groupChats, contacts, adminName, assistantMessage, latestUserMessage]);

  const handleSend = (key: string, payload: MedicalNotificationPayload) => {
    setActiveKey(key);
    const resolvedPayload: MedicalNotificationPayload = {
      ...payload,
      ...(userDetails?.id
        ? { targetUserId: userDetails.id, requestingUserId: userDetails.id }
        : {}),
    };
    mutate(resolvedPayload, {
      onSettled: () => setActiveKey(null),
    });
  };

  return (
    <Card className="mb-12 w-full" variant="cream" elevation={2}>
      <Card.Header variant="cream" className="bg-cream-100">
        <div className="flex flex-col gap-2">
          <Typography variant="h4" className="text-terracotta">
            Notification Examples
          </Typography>
          <Typography variant="body2" className="text-brown-600">
            Send representative notification payloads to your device to verify
            formatting, metadata, and downstream automations.
          </Typography>
        </div>
      </Card.Header>
      <Card.Body className="space-y-4 bg-white">
        {examples.map(([key, payload]) => {
          const label = exampleLabels[key] ?? key;
          const isActive = isPending && activeKey === key;
          return (
            <div
              key={key}
              className="flex flex-col gap-2 rounded-xl border border-cream-200 p-4 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <Typography variant="subtitle1" className="text-terracotta-700">
                  {label}
                </Typography>
                <Typography variant="caption" className="text-brown-500">
                  {payload.intent ?? "No intent"} · {payload.entityType}
                </Typography>
                {payload.metadata && (
                  <pre className="mt-2 overflow-x-auto rounded bg-cream-50 p-2 text-xs text-brown-700">
                    {JSON.stringify(payload.metadata, null, 2)}
                  </pre>
                )}
              </div>
              <SubmitButton
                size="sm"
                onClick={() => handleSend(key, payload)}
                isDisabled={isActive}
                isLoading={isActive}
                hideArrow
              >
                Send payload
              </SubmitButton>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
}
